import React, { useRef, useEffect, useState } from "react";

function RichTextField({
  value,
  onChange,
  label,
  name,
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
  name: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [htmlMode, setHtmlMode] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [showColorDropdown, setShowColorDropdown] = useState(false);

  // Update innerHTML when value or htmlMode changes (especially when switching to formatted view)
  useEffect(() => {
    if (!htmlMode && ref.current && ref.current.innerHTML !== value) {
      ref.current.innerHTML = value;
    }
  }, [value, htmlMode]);

  const handleInput = () => {
    if (ref.current) onChange(ref.current.innerHTML);
  };

  const handleButton = (cmd: string, arg?: string) => {
    if (cmd === "createLink") {
      const url = prompt("Enter URL:");
      if (url) document.execCommand(cmd, false, url);
    } else if (cmd === "foreColor") {
      setShowColorDropdown((v) => !v);
      return;
    } else {
      document.execCommand(cmd, false, arg || undefined);
      handleInput();
    }
  };

  const handleColorSelect = (color: string) => {
    document.execCommand("foreColor", false, color);
    setShowColorDropdown(false);
    handleInput();
  };

  const toolbarButtons = [
    { cmd: "bold", icon: "B" },
    { cmd: "italic", icon: "I" },
    { cmd: "underline", icon: "U" },
    { cmd: "foreColor", icon: "A" },
    { cmd: "insertOrderedList", icon: "1." },
    { cmd: "insertUnorderedList", icon: "•" },
    { cmd: "outdent", icon: "ᐊ" },  
    { cmd: "indent", icon: "ᐅ" },      
    { cmd: "createLink", icon: "🔗" },
  ];

  const colorOptions = [
    { name: "Black", value: "#000000" },
    { name: "Red", value: "#e53935" },
    { name: "Green", value: "#43a047" },
    { name: "Orange", value: "#fb8c00" },
  ];

  return (
    <div style={{ marginBottom: 0, position: "relative" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <label style={{ fontWeight: "normal", color: "#888", fontSize: "0.9rem"}}>{label}</label>
        {isActive && (
          <div style={{ display: "flex", gap: 4, position: "relative" }}>
            {toolbarButtons.map(btn =>
              btn.cmd === "foreColor" ? (
                <div key={btn.cmd} style={{ position: "relative" }}>
                  <button
                    type="button"
                    style={{
                      fontWeight: "bold",
                      fontSize: 16,
                      padding: "2px 6px",
                      borderRadius: 4,
                      border: "1px solid #ccc",
                      background: "#fff",
                      cursor: "pointer"
                    }}
                    onMouseDown={e => {
                      e.preventDefault();
                      handleButton(btn.cmd);
                    }}
                    title="Text color"
                  >
                    {btn.icon}
                  </button>
                  {showColorDropdown && (
                    <div
                      style={{
                        position: "absolute",
                        top: "110%",
                        right: 0,
                        background: "#fff",
                        border: "1px solid #ccc",
                        borderRadius: 4,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                        zIndex: 10,
                        minWidth: 90,
                      }}
                    >
                      {colorOptions.map(opt => (
                        <div
                          key={opt.value}
                          onMouseDown={e => {
                            e.preventDefault();
                            handleColorSelect(opt.value);
                          }}
                          style={{
                            padding: "6px 12px",
                            cursor: "pointer",
                            color: opt.value,
                            fontWeight: "bold",
                            background: "none",
                            border: "none",
                          }}
                        >
                          {opt.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <button
                  key={btn.cmd}
                  type="button"
                  style={{
                    fontWeight: "bold",
                    fontSize: 16,
                    padding: "2px 6px",
                    borderRadius: 4,
                    border: "1px solid #ccc",
                    background: "#fff",
                    cursor: "pointer"
                  }}
                  onMouseDown={e => { e.preventDefault(); handleButton(btn.cmd); }}
                  title={btn.cmd === "indent" ? "Indent (sublist)" : btn.cmd === "outdent" ? "Outdent (remove sublist)" : btn.cmd}
                >
                  {btn.icon}
                </button>
              )
            )}
            {/* Clear formatting button */}
            <button
              type="button"
              style={{
                fontWeight: "bold",
                fontSize: 16,
                padding: "2px 6px",
                borderRadius: 4,
                border: "1px solid #ccc",
                background: "#fff",
                color: "#e53935",
                cursor: "pointer",
                marginLeft: 4,
              }}
              title="Remove all formatting"
              onMouseDown={e => {
                e.preventDefault();
                if (ref.current) {
                  // Get plain text, preserve line breaks
                  const plain = ref.current.innerText.replace(/\r\n|\r|\n/g, "<br>");
                  ref.current.innerHTML = plain;
                  onChange(ref.current.innerHTML);
                }
              }}
            >
              ⎚
            </button>
            <button
              type="button"
              style={{
                marginLeft: 8,
                fontSize: 12,
                borderRadius: 4,
                border: "1px solid #ccc",
                background: "#f5f6fa",
                cursor: "pointer"
              }}
              onMouseDown={e => {
                e.preventDefault();
                setHtmlMode(!htmlMode);
                setShowColorDropdown(false);
              }}
            >
              {htmlMode ? "Formatted" : "HTML"}
            </button>
          </div>
        )}
      </div>
      {htmlMode ? (
        <textarea
          name={name}
          value={value}
          onChange={e => onChange(e.target.value)}
          rows={3}
          onFocus={() => setIsActive(true)}
          onBlur={() => {
            setIsActive(false);
            setShowColorDropdown(false);
          }}
          style={{
            marginTop: "10px",
            padding: "12px",
            borderRadius: "8px",
            border: "1px solid #ccc",
            fontSize: "0.9rem",
            width: "97%",
            fontFamily: "monospace",
          }}
        />
      ) : (
        <div
          ref={ref}
          contentEditable
          suppressContentEditableWarning
          className="rich-text-editor"
          onInput={handleInput}
          onFocus={() => setIsActive(true)}
          onBlur={() => {
            setIsActive(false);
            setShowColorDropdown(false);
          }}
          style={{
            minHeight: 60,
            padding: "12px",
            borderRadius: "8px",
            border: "1px solid #ccc",
            fontSize: "0.9rem",
            background: "#fff",
            fontFamily: "inherit",
            marginTop: "10px",
            resize: "vertical",
            overflow: "auto",
          }}
        />
      )}
    </div>
  );
}

export default RichTextField;